/**
 * Refactored Zustand Store for Building Administration 3D Spatial Violation Management System
 * Modular state management with separated slices
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AuthSlice } from './slices/authSlice';
import type { UISlice } from './slices/uiSlice';

// Import the actual slice creators
import { createAuthSlice as authSlice } from './slices/authSlice';
import { createUISlice as uiSlice } from './slices/uiSlice';

// Combined store type
type AppStore = AuthSlice &
  UISlice & {
    // Utility actions
    resetStore: () => void;
  };

export const useAppStore = create<AppStore>()(
  persist(
    (...args) => ({
      ...authSlice(...args),
      ...uiSlice(...args),

      // Utility actions
      resetStore: () => {
        const [set] = args;
        set({
          // Reset auth state
          auth: {
            user: null,
            isLoading: false,
            error: null,
          },
          // Reset UI state
          ui: {
            sidebarOpen: true,
            isMobile: false,
            notifications: [],
            uploadTasks: [],
            modal: { open: false, type: null, data: null },
          },
          // Reset offline state
          offline: {
            isOnline: navigator.onLine,
            pendingSync: [],
            lastSync: null,
          },
        });
      },
    }),
    {
      name: 'land-administration-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        ui: {
          sidebarOpen: state.ui.sidebarOpen,
        },
        offline: {
          lastSync: state.offline.lastSync,
        },
      }),
      merge: (persistedState: unknown, currentState: AppStore): AppStore => {
        const persisted = persistedState as Partial<AppStore> | null;

        return {
          ...currentState,
          ...(persisted || {}),
          ui: {
            ...currentState.ui,
            ...(persisted?.ui || {}),
            uploadTasks: currentState.ui.uploadTasks || [],
            notifications: currentState.ui.notifications || [],
          },
        };
      },
    }
  )
);

export default useAppStore;
